import React, { useCallback, useEffect, useState } from 'react';
import ProductCartCounter from '../../components/ProductCartCounter';
import { getHairSalonApiBaseUrl } from '../../utils/hairSalonApiDeps';
import {
  buildTreatmentCartPayload,
  persistTreatmentCart,
  scheduleSyncAnalysisPendingServices,
  treatmentCartToPendingServices,
} from '../../utils/hairSalonFlowDeps';
import {
  treatmentRowCartKey,
  treatmentDisplayTitle,
  treatmentRecommendationLine,
} from '../../utils/hairSalonTreatmentUtils';

const RUPEE = '\u20B9';

function toNumberOrNull(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function formatPrice(amount) {
  if (amount == null || amount === '') return '-';
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return `${RUPEE}${n.toFixed(0)}`;
}

function buildTreatmentDetailSections(item) {
  const sections = [];
  const push = (label, value) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      const list = value.map((v) => String(v || '').trim()).filter(Boolean);
      if (!list.length) return;
      sections.push({ label, value: list.join(', '), list });
      return;
    }
    const text = String(value).trim();
    if (!text) return;
    sections.push({ label, value: text });
  };
  push('About', item?.about_treatment || item?.description);
  push('Key benefit', item?.major_usp);
  push('Kit / Product', item?.kit);
  push('Core ingredients', item?.core_ingredients || item?.ingredient);
  push('Suggested post care', item?.post_care);
  push('Rate card', item?.rate_card);
  push('Category', item?.category);
  push('Brand', item?.brand);
  push('Addresses', item?.conditions || item?.suggested_for_conditions);
  push('Goals', item?.skin_goals);
  const priceBits = [];
  if (item?.net_price != null && item?.net_price !== '') priceBits.push(`₹${item.net_price}`);
  if (
    item?.sale_price != null &&
    item?.sale_price !== '' &&
    String(item.sale_price) !== String(item.net_price)
  ) {
    priceBits.push(`sale ₹${item.sale_price}`);
  }
  if (item?.price && !priceBits.length) priceBits.push(String(item.price));
  if (item?.amount != null && item?.amount !== '' && !priceBits.length) {
    priceBits.push(`₹${item.amount}`);
  }
  if (priceBits.length) push('Price', priceBits.join(' · '));
  return sections;
}

export default function HairInsightReportTreatmentsSection({
  treatments = [],
  loading = false,
  error = '',
  sessionId,
  token,
  treatmentCartItems,
  onTreatmentCartChange,
  onRetry,
}) {
  const list = Array.isArray(treatments) ? treatments : [];
  const [detailPopup, setDetailPopup] = useState({
    open: false,
    title: '',
    text: '',
    sections: [],
  });

  const handleQty = useCallback(
    (item, cartKey, quantity) => {
      if (typeof onTreatmentCartChange !== 'function') return;
      onTreatmentCartChange((prev) => {
        const next = { ...(prev || {}) };
        if (!quantity) {
          delete next[cartKey];
        } else {
          next[cartKey] = { treatment: item, quantity };
        }
        return next;
      });
    },
    [onTreatmentCartChange],
  );

  useEffect(() => {
    const lines = treatmentCartToPendingServices(treatmentCartItems, 'hair_analysis');
    scheduleSyncAnalysisPendingServices(lines, 'hair_analysis');
  }, [treatmentCartItems]);

  useEffect(() => {
    const base = getHairSalonApiBaseUrl();
    if (!base || !sessionId || !treatmentCartItems) return;
    const lines = buildTreatmentCartPayload(treatmentCartItems);
    if (!lines.length) return;
    const timer = window.setTimeout(() => {
      persistTreatmentCart(base, sessionId, token, treatmentCartItems);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [treatmentCartItems, sessionId, token]);

  const openTreatmentDetails = (item) => {
    const title = treatmentDisplayTitle(item) || 'Treatment details';
    const sections = buildTreatmentDetailSections(item);
    const text =
      item?.about_treatment ||
      item?.description ||
      item?.major_usp ||
      'No additional details available.';
    setDetailPopup({ open: true, title, text, sections });
  };

  const closeTreatmentDetails = () =>
    setDetailPopup({ open: false, title: '', text: '', sections: [] });

  return (
    <div className="skin-report-section skin-report-reco-section hi-report-treatments-section" id="section-hair-treatments">
      <h2 className="skin-report-section-heading">
        <span className="skin-report-section-num" aria-hidden="true">5</span>
        <span className="skin-report-section-icon" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v4M8 7h8l1 4H7l1-4z"/><path d="M7 11v8a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-8"/></svg>
        </span>
        Personalized Services
      </h2>
      <div className="skin-report-reco">
        {loading ? (
          <div className="skin-routine-summary-loading">
            <div className="loading-spinner" />
            <p className="skin-routine-summary-loading-text">Generating recommended services...</p>
          </div>
        ) : error ? (
          <div className="skin-routine-summary-error-wrap">
            <p className="skin-routine-summary-text" role="status">{error}</p>
            {onRetry ? (
              <button type="button" className="skin-action-button skin-action-button-secondary" onClick={onRetry}>
                Retry services
              </button>
            ) : null}
          </div>
        ) : list.length > 0 ? (
          <div className="skin-routine-treatments-grid">
            {list.slice(0, 8).map((item, idx) => {
              const title = treatmentDisplayTitle(item, idx);
              const cartKey = treatmentRowCartKey(item, idx);
              const cartQuantity = treatmentCartItems?.[cartKey]?.quantity || 0;
              const why = treatmentRecommendationLine(item);
              const aboutText = item?.about_treatment || item?.description || '';
              const majorUsp = item?.major_usp ? String(item.major_usp).trim() : '';
              const hasNetPrice = toNumberOrNull(item?.net_price) != null;
              const hasAmountPrice = toNumberOrNull(item?.amount) != null;
              const priceText = String(item?.price || '').trim();
              const hasAnyPrice = hasNetPrice || hasAmountPrice || Boolean(priceText);
              const net = toNumberOrNull(item?.net_price);
              const sale = toNumberOrNull(item?.sale_price);
              const amount = toNumberOrNull(item?.amount);
              const sessionsForPrice = Math.max(1, cartQuantity || 0);
              const serviceType = String(item?.service_type || '').replace('_', ' ');
              const category = item?.category || '';

              return (
                <div key={cartKey} className="skin-routine-treatment-card skin-routine-treatment-card--text">
                  <div className="skin-routine-treatment-body">
                    <div className="skin-routine-treatment-text-head">
                      <div className="skin-routine-treatment-title">{title}</div>
                      {(serviceType || category) ? (
                        <div className="skin-routine-treatment-meta-chips">
                          {serviceType ? (
                            <span className="skin-routine-treatment-chip">{serviceType}</span>
                          ) : null}
                          {category ? (
                            <span className="skin-routine-treatment-chip">{category}</span>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    {why ? (
                      <div className="skin-routine-treatment-row">
                        <span className="skin-routine-treatment-mini-heading">Why recommended</span>
                        <div className="skin-routine-treatment-why-text">{why}</div>
                      </div>
                    ) : null}
                    {majorUsp || aboutText ? (
                      <div className="skin-routine-treatment-row">
                        <span className="skin-routine-treatment-mini-heading">About</span>
                        <div className="skin-routine-treatment-usp-text">
                          {majorUsp || aboutText}
                        </div>
                      </div>
                    ) : null}
                    {item?.conditions?.length > 0 ? (
                      <div className="skin-routine-treatment-tags-area">
                        <div className="skin-routine-treatment-tags-wrap">
                          <span className="skin-routine-treatment-mini-heading">Addresses</span>
                          <div className="skin-routine-treatment-tags">
                            {item.conditions.map((c, i) => (
                              <span
                                key={`${c}-${i}`}
                                className="skin-routine-treatment-tag skin-routine-treatment-tag--condition"
                              >
                                {c}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="skin-routine-treatment-view-details"
                      onClick={() => openTreatmentDetails(item)}
                    >
                      View details
                    </button>
                  </div>
                  <div className="skin-routine-treatment-footer">
                    <div className="skin-routine-treatment-price-block">
                      {hasAnyPrice ? (
                        hasNetPrice || hasAmountPrice ? (
                          <span className="skin-routine-treatment-price">
                            {formatPrice((sale ?? net ?? amount ?? 0) * sessionsForPrice)}
                          </span>
                        ) : (
                          <span className="skin-routine-treatment-price">{priceText}</span>
                        )
                      ) : (
                        <span className="skin-routine-treatment-price-free">Ask front desk</span>
                      )}
                    </div>
                    <div className="skin-routine-treatment-cta">
                      <ProductCartCounter
                        product={item}
                        onAddToCart={(t, quantity) => handleQty(t, cartKey, quantity)}
                        initialQuantity={cartQuantity}
                        quantityLabel={hasAmountPrice ? 'Sessions' : 'Qty'}
                        addLabel="Select Service"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="skin-report-treatments-empty">
            <p className="skin-routine-summary-text">
              {error
                ? error
                : 'No priced services matched this outlet list yet. Add hair services in Partner → Service list (Find from HQ → Hair & Spa), or tap Retry.'}
            </p>
            {onRetry ? (
              <button type="button" className="skin-report-treatments-retry" onClick={onRetry} disabled={loading}>
                Retry services
              </button>
            ) : null}
          </div>
        )}
        <p className="skin-report-products-note">
          Select services for this visit, then confirm at the bottom to start them at the stylist desk.
        </p>
      </div>

      {detailPopup.open ? (
        <div className="skin-routine-about-popup-backdrop" onClick={closeTreatmentDetails}>
          <div className="skin-routine-about-popup" onClick={(e) => e.stopPropagation()}>
            <div className="skin-routine-about-popup-header">
              <h3 className="skin-routine-about-popup-title">{detailPopup.title}</h3>
              <button
                type="button"
                className="skin-routine-about-popup-close"
                onClick={closeTreatmentDetails}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="skin-routine-about-popup-body">
              {Array.isArray(detailPopup.sections) && detailPopup.sections.length > 0 ? (
                <div className="skin-routine-about-popup-sections">
                  {detailPopup.sections.map((section) => (
                    <div key={section.label} className="skin-routine-about-popup-section">
                      <h4 className="skin-routine-about-popup-section-label">{section.label}</h4>
                      {Array.isArray(section.list) ? (
                        <div className="skin-routine-treatment-tags">
                          {section.list.map((tag) => (
                            <span
                              key={`${section.label}-${tag}`}
                              className="skin-routine-treatment-tag skin-routine-treatment-tag--condition"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="skin-routine-about-popup-section-text">{section.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="skin-routine-about-popup-section-text">{detailPopup.text}</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
